import { useState } from "react";
import { toast } from "sonner";
import { Trash2, Send, FileDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api, apiErr } from "@/lib/api";
import { MATERIAL_TYPES } from "@/lib/constants";
import { FolderSelect } from "@/components/dashboard/DashboardParts";

function MaterialSelect({ value, onChange }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid="material-select"><SelectValue /></SelectTrigger>
      <SelectContent>
        {MATERIAL_TYPES.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

export function UploadDialog({ folders, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [author, setAuthor] = useState("");
  const [folderId, setFolderId] = useState("");
  const [materialType, setMaterialType] = useState("book");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!file || !title) return toast.error("File and title required");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", title);
      fd.append("author", author);
      fd.append("folder_id", folderId);
      fd.append("material_type", materialType);
      await api.post("/books/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded");
      onDone();
      onClose();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const onFile = (e) => {
    const f = e.target.files[0];
    setFile(f);
    if (f && !title) setTitle(f.name.replace(/\.[^.]+$/, ""));
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-head">Upload a document</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>File (PDF, EPUB, MOBI, MP3, MP4)</Label>
            <Input data-testid="upload-file" type="file" accept=".pdf,.epub,.mobi,.mp3,.mp4" className="mt-1" onChange={onFile} />
          </div>
          <div><Label>Title</Label><Input data-testid="upload-title" value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1" /></div>
          <div><Label>Author</Label><Input data-testid="upload-author" value={author} onChange={(e) => setAuthor(e.target.value)} className="mt-1" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Type</Label><div className="mt-1"><MaterialSelect value={materialType} onChange={setMaterialType} /></div></div>
            <div><Label>Folder</Label><div className="mt-1"><FolderSelect folders={folders} value={folderId} onChange={setFolderId} /></div></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="upload-submit" onClick={submit} disabled={busy}>{busy ? "Uploading…" : "Upload"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function FolderRenameDialog({ folder, onClose, onDone }) {
  const [name, setName] = useState(folder.name);
  const submit = async () => {
    if (!name.trim()) return;
    await api.put(`/folders/${folder.id}`, { name: name.trim() });
    toast.success("Folder renamed");
    onDone();
    onClose();
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-head">Rename folder</DialogTitle></DialogHeader>
        <div><Label>Name</Label><Input data-testid="folder-rename-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1"
          onKeyDown={(e) => e.key === "Enter" && submit()} autoFocus /></div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="folder-rename-submit" onClick={submit}>Rename</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function FolderDialog({ folders, onClose, onDone }) {
  const [name, setName] = useState("");
  const [parent, setParent] = useState("");
  const submit = async () => {
    if (!name) return;
    await api.post("/folders", { name, parent_id: parent || null });
    toast.success("Folder created");
    onDone();
    onClose();
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-head">New folder</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Name</Label><Input data-testid="folder-name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1" /></div>
          <div><Label>Parent folder (optional — creates a subfolder)</Label><div className="mt-1"><FolderSelect folders={folders} value={parent} onChange={setParent} /></div></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="folder-submit" onClick={submit}>Create</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function EditBookDialog({ book, folders, onClose, onDone }) {
  const [title, setTitle] = useState(book.title);
  const [author, setAuthor] = useState(book.author || "");
  const [folderId, setFolderId] = useState(book.folder_id || "");
  const [materialType, setMaterialType] = useState(book.material_type || "book");
  const [cover, setCover] = useState(null);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/books/${book.id}`, { title, author, folder_id: folderId || null, material_type: materialType });
      if (cover) {
        const fd = new FormData();
        fd.append("file", cover);
        await api.post(`/books/${book.id}/cover`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success("Saved");
      onDone();
      onClose();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  const del = async () => {
    await api.delete(`/books/${book.id}`);
    toast.success("Deleted");
    onDone();
    onClose();
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-head">Edit document</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Title</Label><Input data-testid="edit-title" value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1" /></div>
          <div><Label>Author</Label><Input data-testid="edit-author" value={author} onChange={(e) => setAuthor(e.target.value)} className="mt-1" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Type</Label><div className="mt-1"><MaterialSelect value={materialType} onChange={setMaterialType} /></div></div>
            <div><Label>Folder</Label><div className="mt-1"><FolderSelect folders={folders} value={folderId} onChange={setFolderId} /></div></div>
          </div>
          <div><Label>Cover image</Label><Input data-testid="edit-cover" type="file" accept="image/*" className="mt-1" onChange={(e) => setCover(e.target.files[0])} /></div>
        </div>
        <DialogFooter className="justify-between sm:justify-between">
          <Button data-testid="delete-book" variant="destructive" onClick={del}><Trash2 className="h-4 w-4 mr-1.5" />Delete</Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button data-testid="edit-save" onClick={save} disabled={busy}>{busy ? "Saving…" : "Save"}</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ListDialog({ selected, selectedFolder, folders, onClose, onDownload }) {
  const hasSelection = selected.size > 0;
  const folderName = folders.find((f) => f.id === selectedFolder)?.name;
  const [title, setTitle] = useState(selectedFolder ? `${folderName} — Recommended` : "Recommended Reading List");
  const submit = () => {
    const body = { title };
    if (hasSelection) body.book_ids = Array.from(selected);
    else if (selectedFolder) body.folder_id = selectedFolder;
    onDownload(body, title);
    onClose();
  };
  const desc = hasSelection
    ? `Using ${selected.size} selected book(s).`
    : selectedFolder
    ? "Using all books in the current folder (and subfolders), grouped by folder."
    : "Using all books in your library, grouped by folder.";
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-head">Create reading list PDF</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500">{desc} Each entry includes title, author and an Amazon purchase link.</p>
        <div><Label>List title</Label><Input data-testid="list-title" value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1" /></div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="list-download" onClick={submit}><FileDown className="h-4 w-4 mr-1.5" />Download PDF</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function LendDialog({ book, onClose }) {
  const [email, setEmail] = useState("");
  const [weeks, setWeeks] = useState("2");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!email) return toast.error("Email required");
    setBusy(true);
    try {
      const { data } = await api.post("/borrow/invite", { email, book_id: book.id, weeks: parseInt(weeks) });
      setResult(data);
      toast.success(data.email_sent ? "Invite emailed" : "Invite link created");
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-head">Lend "{book.title}"</DialogTitle></DialogHeader>
        {!result ? (
          <div className="space-y-3">
            <div><Label>Borrower's email</Label><Input data-testid="lend-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1" /></div>
            <div>
              <Label>Borrow duration</Label>
              <Select value={weeks} onValueChange={setWeeks}>
                <SelectTrigger data-testid="lend-weeks" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="2">2 weeks</SelectItem>
                  <SelectItem value="3">3 weeks</SelectItem>
                  <SelectItem value="4">4 weeks</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-sm text-slate-600">{result.email_sent ? "An invite email was sent. You can also share this link:" : "Share this invite link with the borrower:"}</p>
            <div className="flex gap-2">
              <Input data-testid="lend-link" readOnly value={result.link} />
              <Button onClick={() => { navigator.clipboard.writeText(result.link); toast.success("Copied"); }}>Copy</Button>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{result ? "Done" : "Cancel"}</Button>
          {!result && <Button data-testid="lend-submit" onClick={submit} disabled={busy}><Send className="h-4 w-4 mr-1.5" />{busy ? "Sending…" : "Send invite"}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
